/**
 * Engineer impersonation grant API client (spec 048) — web platform.
 *
 * Thin wrapper over bare `fetch('/v1/internal/impersonation/*')` calls,
 * following the same pattern as `use-sdk-wallets.tsx`: relative `/v1/...`
 * URLs flow through the SDK's fetch-shim (installed by `SDKProvider`),
 * which injects auth headers and rewrites paths. Never imports
 * `@morfi/backend-sdk` (v1) — that would violate SDK layering.
 *
 * Behaviour on 401/403: returns empty/benign (never throws-to-crash).
 * All grant-not-found / expired cases surface as `GrantApiClientError`
 * with a typed `.code` the caller switches on.
 *
 * Relocated from `frontend/src/lib/impersonation/api-client.ts` as part
 * of the auth cutover (spec 009, FILE 14, Group F).
 */

import type {
  GrantApiErrorCode,
  GrantApiErrorPayload,
  GrantStatusResponse,
  MintGrantResponse,
  ResolveTargetResponse,
} from '../../../../auth/impersonation/types.js';

const GRANT_HEADER_NAME = 'X-Morfi-Impersonate-Grant';

// ── Error class ───────────────────────────────────────────────────────────────

/** Typed error thrown by the api-client wrappers. The `code` field
 *  matches the backend's discriminated union. */
export class GrantApiClientError extends Error {
  readonly code: GrantApiErrorCode;
  readonly status: number;
  /** Only populated when `code === 'IMPERSONATION_RATE_LIMIT_EXCEEDED'`. */
  readonly retryAfterSeconds: number | null;

  constructor(payload: GrantApiErrorPayload, status: number) {
    super(payload.message ?? payload.code);
    this.name = 'GrantApiClientError';
    this.code = payload.code;
    this.status = status;
    this.retryAfterSeconds =
      payload.code === 'IMPERSONATION_RATE_LIMIT_EXCEEDED' ? payload.retry_after_seconds : null;
  }
}

// ── Response parsing helpers ──────────────────────────────────────────────────

const KNOWN_GRANT_API_CODES: ReadonlySet<GrantApiErrorCode> = new Set([
  'IMPERSONATION_REASON_REQUIRED',
  'IMPERSONATION_ALLOWLIST_DENIED',
  'IMPERSONATION_RATE_LIMIT_EXCEEDED',
  'IMPERSONATION_GRANT_INVALID',
  'IMPERSONATION_GRANT_EXPIRED',
] as const);

function asGrantApiErrorCode(value: string): GrantApiErrorCode | null {
  if ((KNOWN_GRANT_API_CODES as ReadonlySet<string>).has(value)) {
    return value as GrantApiErrorCode;
  }
  return null;
}

function readNumberField(json: unknown, key: string): number | null {
  if (json === null || typeof json !== 'object') return null;
  const v = (json as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : null;
}

function readStringField(json: unknown, key: string): string | null {
  if (json === null || typeof json !== 'object') return null;
  const v = (json as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : null;
}

/**
 * Parse a non-OK response body and throw a `GrantApiClientError` if the
 * backend emitted a known grant-API error code. Re-throw a plain `Error`
 * for everything else (infra errors, unexpected status codes).
 */
async function throwGrantApiError(res: Response): Promise<never> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // SILENT-CATCH: body may not be JSON (e.g. 502 plain-text gateway error).
    throw new Error(`Impersonation API request failed: HTTP ${res.status}`);
  }

  // Backend error envelope: { error: { code, message, details } }
  const error =
    body !== null && typeof body === 'object' && 'error' in body
      ? (body as { error: unknown }).error
      : null;

  const code = readStringField(error, 'code') ?? readStringField(body, 'code');
  const rawMessage = readStringField(error, 'message') ?? readStringField(body, 'message');

  if (code !== null) {
    const typed = asGrantApiErrorCode(code);
    if (typed !== null) {
      if (typed === 'IMPERSONATION_RATE_LIMIT_EXCEEDED') {
        const details =
          error !== null && typeof error === 'object' && 'details' in error
            ? (error as { details: unknown }).details
            : null;
        const retryAfterSeconds = readNumberField(details, 'retry_after_seconds') ?? 0;
        const payload =
          rawMessage !== null
            ? { code: typed, message: rawMessage, retry_after_seconds: retryAfterSeconds }
            : { code: typed, retry_after_seconds: retryAfterSeconds };
        throw new GrantApiClientError(payload, res.status);
      }
      const payload = rawMessage !== null ? { code: typed, message: rawMessage } : { code: typed };
      throw new GrantApiClientError(payload, res.status);
    }
  }

  throw new Error(`Impersonation API request failed: HTTP ${res.status}`);
}

function unwrapData<T>(json: unknown): T {
  if (json !== null && typeof json === 'object' && 'data' in json) {
    return (json as { data: unknown }).data as T;
  }
  return json as T;
}

// ── Grant-token header injection ──────────────────────────────────────────────

/**
 * Add the grant header to this request only. Grant status and revoke requests
 * can overlap any browser fetch, so replacing global `fetch` here would leak
 * the raw token to an unrelated request during that overlap.
 */
function withGrantHeader(init: RequestInit, grantToken: string): RequestInit {
  const headers = new Headers(init.headers);
  headers.set(GRANT_HEADER_NAME, grantToken);
  return { ...init, headers };
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Mint a new impersonation grant.
 *  See `grant-api.md` POST `/v1/internal/impersonation/grants`. */
export async function mintGrant(args: {
  target_identifier: string;
  reason?: string;
}): Promise<MintGrantResponse> {
  const body: Record<string, unknown> = { target_identifier: args.target_identifier };
  if (args.reason !== undefined) body.reason = args.reason;

  const res = await fetch('/v1/internal/impersonation/grants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) await throwGrantApiError(res);

  const json = (await res.json()) as unknown;
  return unwrapData<MintGrantResponse>(json);
}

/** Revoke the currently active grant. Best-effort — idempotent (re-revoking
 *  returns 204). Network / abort errors are swallowed since the backend
 *  will TTL-expire the grant on its own. */
export async function revokeGrant(grantToken: string): Promise<void> {
  try {
    const res = await fetch(
      '/v1/internal/impersonation/grants/current',
      withGrantHeader(
        {
          method: 'DELETE',
          credentials: 'include',
        },
        grantToken,
      ),
    );
    // 204 No Content = success; 404 = already revoked. Both fine.
    if (res.status === 404 || res.status === 204) return;
    if (!res.ok) await throwGrantApiError(res);
  } catch (err: unknown) {
    // SILENT-CATCH: NETWORK_ERROR/TIMEOUT swallowed (best-effort revoke during teardown);
    // typed errors that are not about connectivity rethrow.
    if (err instanceof GrantApiClientError) {
      if (
        err.code === 'IMPERSONATION_GRANT_INVALID' ||
        err.code === 'IMPERSONATION_GRANT_EXPIRED'
      ) {
        // Already gone server-side — fine.
        return;
      }
      throw err;
    }
    // Network / abort / TypeError → swallow (best-effort).
  }
}

/** Look up the current grant status. Used on page reload to verify the
 *  sessionStorage grant is still valid.
 *  Returns `null` on 401/403 (benign — not throwing-to-crash).
 *
 *  SPEC 009 FILE 14: the read `GET /v1/internal/impersonation/grants/current`
 *  was collapsed into `GET /v1/me?kind=impersonation` — response
 *  `{ data: { kind: 'impersonation', grant: <status|null> } }`. The grant
 *  token still rides the request header (`withGrantHeader`); the DELETE
 *  revoke route stays under `/v1/internal/`. */
export async function getGrantStatus(grantToken: string): Promise<GrantStatusResponse | null> {
  try {
    const res = await fetch(
      '/v1/me?kind=impersonation',
      withGrantHeader(
        {
          credentials: 'include',
        },
        grantToken,
      ),
    );
    // 401/403 → grant no longer valid; return null (benign).
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) await throwGrantApiError(res);
    const json = (await res.json()) as unknown;
    const arm = unwrapData<{ kind: 'impersonation'; grant: GrantStatusResponse | null }>(json);
    return arm.grant;
  } catch (err: unknown) {
    // SILENT-CATCH: rethrow typed grant errors so the caller can handle them;
    // infra errors propagate as plain Error.
    if (err instanceof GrantApiClientError) throw err;
    throw err;
  }
}

/** Resolve a target identifier (wallet address or user id) to a preview.
 *  Read-only — no grant minted, no DB mutation.
 *  Returns `null` on 401/403. */
export async function resolveTarget(
  target_identifier: string,
): Promise<ResolveTargetResponse | null> {
  const res = await fetch('/v1/internal/impersonation/grants/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ target_identifier }),
  });

  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) await throwGrantApiError(res);

  const json = (await res.json()) as unknown;
  return unwrapData<ResolveTargetResponse>(json);
}
